import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toStorySlug } from '../../../../lib/slugify';

interface Story {
  id: string;
  title: string;
  genre: string;
  datePublished: string;
  readCount: number;
  likeCount: number;
  status: string;
}

interface StoriesTabProps {
  stories: Story[];
}

function StoryMenu({ story, onClose }: { story: Story; onClose: () => void }) {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-10 z-50 w-44 bg-zinc-900 border border-red-900/40 rounded-lg shadow-xl overflow-hidden"
    >
      <button
        onClick={() => {
          navigate(`/submit?edit=${story.id}`);
          onClose();
        }}
        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
        style={{ fontFamily: 'Crimson Text, serif' }}
      >
        <i className="ri-edit-line text-red-400"></i>
        Edit Story
      </button>
      {story.status === 'published' && (
        <Link
          to={`/story/${toStorySlug(story.title, story.id)}`}
          onClick={onClose}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          style={{ fontFamily: 'Crimson Text, serif' }}
        >
          <i className="ri-eye-line text-red-400"></i>
          View Story
        </Link>
      )}
    </div>
  );
}

export default function StoriesTab({ stories }: StoriesTabProps) {
  const [activeTab, setActiveTab] = useState<'published' | 'draft'>('published');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const navigate = useNavigate();

  const published = stories.filter((s) => s.status === 'published');
  const drafts = stories.filter((s) => s.status === 'draft');
  const displayed = activeTab === 'published' ? published : drafts;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not published';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-zinc-900 border border-red-900/30 rounded-full p-1 w-fit">
        {(['published', 'draft'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer capitalize ${
              activeTab === tab
                ? 'bg-red-900 text-zinc-100'
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            {tab === 'published' ? 'Published' : 'Drafts'}
            <span className="ml-2 text-xs opacity-70">
              ({tab === 'published' ? published.length : drafts.length})
            </span>
          </button>
        ))}
      </div>

      {/* Story list */}
      <div className="space-y-4">
        {displayed.length === 0 ? (
          <div className="text-center py-16 text-zinc-500" style={{ fontFamily: 'Crimson Text, serif' }}>
            {activeTab === 'draft'
              ? 'No drafts yet. Start writing and save as draft to continue later.'
              : 'No published stories yet.'}
          </div>
        ) : (
          displayed.map((story) => (
            <div
              key={story.id}
              className="bg-zinc-900 border border-red-900/30 rounded-lg p-6 hover:border-red-900/50 transition-all duration-300"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {story.status === 'published' ? (
                      <Link
                        to={`/story/${toStorySlug(story.title, story.id)}`}
                        className="text-2xl font-bold text-zinc-100 hover:text-red-400 transition-colors cursor-pointer"
                        style={{ fontFamily: 'Cinzel, serif' }}
                      >
                        {story.title}
                      </Link>
                    ) : (
                      <h3
                        className="text-2xl font-bold text-zinc-100"
                        style={{ fontFamily: 'Cinzel, serif' }}
                      >
                        {story.title}
                      </h3>
                    )}
                    {story.status === 'draft' && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-900/40 text-amber-400 border border-amber-700/40 whitespace-nowrap">
                        Draft
                      </span>
                    )}
                  </div>
                  <div
                    className="flex flex-wrap items-center gap-4 text-sm text-zinc-400"
                    style={{ fontFamily: 'Crimson Text, serif' }}
                  >
                    <span className="flex items-center gap-1">
                      <i className="ri-price-tag-3-line"></i>
                      {story.genre}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {story.status === 'draft' ? `Last edited ${formatDate(story.datePublished)}` : formatDate(story.datePublished)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-zinc-300">
                  {story.status === 'published' && (
                    <>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-eye-line text-lg"></i>
                        </div>
                        <span className="text-base" style={{ fontFamily: 'Crimson Text, serif' }}>
                          {formatNumber(story.readCount)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-heart-line text-lg"></i>
                        </div>
                        <span className="text-base" style={{ fontFamily: 'Crimson Text, serif' }}>
                          {formatNumber(story.likeCount)}
                        </span>
                      </div>
                    </>
                  )}

                  {story.status === 'draft' && (
                    <button
                      onClick={() => navigate(`/submit?draft=${story.id}`)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-900/30 hover:bg-red-900/60 border border-red-800/50 text-red-300 rounded-md text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: 'Crimson Text, serif' }}
                    >
                      <i className="ri-edit-line"></i>
                      Continue Writing
                    </button>
                  )}

                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === story.id ? null : story.id)}
                      className="w-8 h-8 flex items-center justify-center hover:bg-zinc-800 rounded-md transition-colors cursor-pointer"
                    >
                      <i className="ri-more-2-fill text-xl text-zinc-400"></i>
                    </button>
                    {openMenuId === story.id && (
                      <StoryMenu
                        story={story}
                        onClose={() => setOpenMenuId(null)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
